/**
 * Scroll an element into view by moving ONLY its own scroll container.
 *
 * `Element.scrollIntoView()` scrolls every scrollable ancestor, the document
 * included. The admin shell is a viewport-height flex layout whose content
 * scrolls in an inner container, so letting the document scroll too pulled the
 * navbar and the legacy-version banner off the top and left empty space below
 * the shell (BRIEFD-7094).
 */
export function scrollIntoContainer(
  element: HTMLElement,
  block: 'center' | 'start' = 'center',
  offsetTop = 0,
): void {
  const container = nearestScrollableAncestor(element);

  if (!container) {
    return;
  }

  const containerTop = container.getBoundingClientRect().top;
  const {top, height} = element.getBoundingClientRect();

  // `offsetTop` reserves room for whatever floats over the top of the container
  // (the advertising gap helper). Centring alone is not enough for a tall field:
  // centring the WRAPPER of a title + rich-text editor + preview puts its label
  // and input above the middle, i.e. underneath the helper — the dealer was sent
  // to a field they then could not see (BRIEFD-7326).
  const centred = top - containerTop - Math.max(0, (container.clientHeight - height) / 2);
  const aligned = top - containerTop - offsetTop;

  // Only centre when the whole field actually fits under the overlay; otherwise
  // put its top just below it, so the label and first input are what you land on.
  const fits = height <= container.clientHeight - offsetTop;
  const wanted = block === 'center' && fits ? Math.min(centred, aligned) : aligned;

  const max = container.scrollHeight - container.clientHeight;

  container.scrollTop = Math.max(0, Math.min(max, container.scrollTop + wanted));
}

/**
 * Whether the element sits in the usable band of its scroll container: below
 * anything overlaying the top (`offsetTop`) and fully above the bottom edge.
 *
 * Lets a caller re-scroll until the target is genuinely on screen, rather than
 * scrolling once and trusting that the layout underneath it holds still.
 *
 * "Fully" matters: this used to answer `true` as soon as the element's TOP was
 * anywhere above the container's bottom edge, so a field whose label had just
 * crept into the last few pixels of the viewport counted as visible and no
 * scroll was made — the wizard announced a field and left the dealer squinting
 * at it clipped against the bottom of the page (BRIEFD-7189). A field taller
 * than the band can never fit, so for those we ask only that the band's worth
 * of it starting at its top is showing.
 */
export function isInContainerView(element: HTMLElement, offsetTop = 0): boolean {
  const container = nearestScrollableAncestor(element);

  if (!container) {
    return true;
  }

  const c = container.getBoundingClientRect();
  const e = element.getBoundingClientRect();
  const band = Math.max(0, container.clientHeight - offsetTop);
  const needed = Math.min(e.height, band);

  return e.top >= c.top + offsetTop - 4 && e.top + needed <= c.bottom + 4;
}

/**
 * The element whose scrollTop moves this one — exported so a caller can tell a
 * scroll IT performed from one the user performed.
 */
export function scrollOwner(element: HTMLElement): HTMLElement | null {
  return nearestScrollableAncestor(element);
}

/** Closest ancestor that actually scrolls. */
function nearestScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;

  while (node) {
    const overflowY = getComputedStyle(node).overflowY;

    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }

    node = node.parentElement;
  }

  return null;
}
